/** Copyright 2026 Midas Healthcare Solutions - All Rights Reserved **/
import { DOCUMENT } from '@angular/common';
import { Inject, Injectable } from '@angular/core';
import { RECAPTCHA_SITE_KEY } from '../tokens/recaptcha-site-key.token';
import { Observable, defer, from, switchMap, throwError } from 'rxjs';

interface GoogleRecaptcha {
  enterprise?: {
    ready(callback: () => void): void;
    execute(siteKey: string, options: { action: string }): Promise<string>;
  };
}

type RecaptchaWindow = Window & { grecaptcha?: GoogleRecaptcha };

@Injectable({ providedIn: 'root' })
export class RecaptchaV3Service {
  private scriptPromise?: Promise<void>;

  constructor(
    @Inject(DOCUMENT) private document: Document,
    @Inject(RECAPTCHA_SITE_KEY) private siteKey: string,
  ) {}

  execute(action: string): Observable<string> {
    return defer(() => {
      if (!this.siteKey?.trim()) {
        return throwError(() => new Error('The reCAPTCHA site key is not configured.'));
      }

      return from(this.loadScript()).pipe(switchMap(() => from(this.executeToken(action))));
    });
  }

  preload(): void {
    void this.loadScript().catch(() => undefined);
  }

  private loadScript(): Promise<void> {
    const captchaWindow = this.document.defaultView as RecaptchaWindow | null;
    if (captchaWindow?.grecaptcha?.enterprise) {
      return Promise.resolve();
    }
    if (this.scriptPromise) {
      return this.scriptPromise;
    }
    if (!captchaWindow) {
      return Promise.reject(new Error('reCAPTCHA is unavailable.'));
    }

    this.scriptPromise = new Promise<void>((resolve, reject) => {
      const script = this.document.createElement('script');
      const timeoutId = setTimeout(() => {
        this.scriptPromise = undefined;
        reject(new Error('Timed out loading reCAPTCHA.'));
      }, 10000);

      script.src = `https://www.google.com/recaptcha/enterprise.js?render=${encodeURIComponent(this.siteKey)}`;
      script.async = true;
      script.defer = true;
      script.onload = () => {
        clearTimeout(timeoutId);
        if (!captchaWindow.grecaptcha?.enterprise) {
          this.scriptPromise = undefined;
          reject(new Error('reCAPTCHA did not initialize.'));
          return;
        }
        resolve();
      };
      script.onerror = () => {
        clearTimeout(timeoutId);
        this.scriptPromise = undefined;
        reject(new Error('Unable to load reCAPTCHA.'));
      };
      this.document.head.appendChild(script);
    });

    return this.scriptPromise;
  }

  private executeToken(action: string): Promise<string> {
    const captchaWindow = this.document.defaultView as RecaptchaWindow | null;
    const grecaptchaEnterprise = captchaWindow?.grecaptcha?.enterprise;
    if (!grecaptchaEnterprise) {
      return Promise.reject(new Error('reCAPTCHA is unavailable.'));
    }

    return new Promise((resolve, reject) => {
      try {
        grecaptchaEnterprise.ready(() => {
          grecaptchaEnterprise.execute(this.siteKey, { action }).then((token) => {
            if (token?.trim()) {
              resolve(token);
            } else {
              reject(new Error('reCAPTCHA returned an empty token.'));
            }
          }, reject);
        });
      } catch (error) {
        reject(error);
      }
    });
  }
}